import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Page,
  LegacyCard,
  IndexTable,
  LegacyStack,
  Pagination,
  Button,
  EmptyState,
  Spinner,
  Badge,
  Banner,
} from "@shopify/polaris";
import qs from "query-string";
import { commaDelimiter, formatMoney, assertNumber, softAssertNumber } from "../../../helper_functions/utils";
import { PurchaseOrdersSearchFieldAndMoreFilters } from "./PurchaseOrdersSearchFieldAndMoreFilters";
import ImportProgressBanner from "../../home/support/ImportProgressBanner";
import { downloadFile, getPurchaseOrders, usePurchaseOrdersTotals, useUser } from "../../../api_utils/requests";
import { formatDateStringToMMDYY } from "../../common/CustomDatePicker/utils";
import { Footer } from "../../common/Footer";
import { PurchaseOrdersIndexPageSkeleton } from "../../common/skeletons";
import type { IndexPurchaseOrder, QueryParams } from "../../../api_utils/types";
import type { NonEmptyArray } from "@shopify/polaris/build/ts/src/types";
import type { IndexTableHeading } from "@shopify/polaris/build/ts/src/components/IndexTable";
import type { Tone } from "@shopify/polaris/build/ts/src/components/Badge";
import HorseButton from "../../common/HorseButton";
import { useVisibleColumns } from "../../../hooks/useVisibleColumns";
import { paymentColumns } from "./paymentColumns";
import { PurchaseOrderPaymentCell } from "./PurchaseOrderPaymentCell";

const columnNames = [
  { title: "Label" },
  { title: "Vendor/Supplier" },
  { title: "Destination" },
  { title: "Status" },
  { title: "Date ordered" },
  { title: "Arrival date" },
  { title: "Requested" },
  { title: "Received" },
  { title: "Cost" },
] satisfies NonEmptyArray<IndexTableHeading>;
const TABS = {
  ALL: 0,
  DRAFT: 1,
  ORDERED: 2,
  CLOSED: 3,
};

const COLLECTIONS = {
  [TABS.ALL]: "all",
  [TABS.DRAFT]: "draft",
  [TABS.ORDERED]: "ordered",
  [TABS.CLOSED]: "closed",
};

const statusColor = (status: IndexPurchaseOrder["status"]): React.ReactElement => {
  let tone: Tone;
  if (status.toLowerCase() === "draft" || status.toLowerCase() === "ordered") {
    tone = "info";
  }
  return <Badge tone={tone}>{status}</Badge>;
};

const purchaseOrderHelpPageUrl =
  "https://horse-inventory.notion.site/Purchase-orders-directory-20399ba15a2443058d30aa5f8a2ec415";

function PurchaseOrdersIndex(): React.ReactElement {
  const { visibleColumns, selectedColumns, setSelectedColumns } = useVisibleColumns("purchase-orders", paymentColumns);
  const [purchaseOrders, setPurchaseOrders] = useState<IndexPurchaseOrder[]>([]);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  const parsedUrlSearch = qs.parse(window.location.search);
  const [filters, setFilters] = useState<QueryParams>(parsedUrlSearch);
  const [pageNum, setPageNum] = useState(softAssertNumber(parsedUrlSearch.page as string) || 1);
  const [collection, setCollection] = useState<number>(
    softAssertNumber(parsedUrlSearch.collection as string) || TABS.ALL,
  );
  const [isLoading, setIsLoading] = useState(true);
  const {
    data: {
      user: { currency: userCurrency },
    },
    isLoading: pageLoading,
  } = useUser();

  const {
    data: totals,
  } = usePurchaseOrdersTotals(filters);

  useEffect(() => {
    setIsLoading(true);
    void getPurchaseOrders({
      ...filters,
      by_state: COLLECTIONS[collection] === COLLECTIONS[TABS.ALL] ? undefined : COLLECTIONS[collection],
      page: pageNum,
    }).then((newPurchaseOrders) => {
      setPurchaseOrders(newPurchaseOrders.rows);
      setHasNext(newPurchaseOrders.hasNext);
      setHasPrev(newPurchaseOrders.hasPrev);
      setIsLoading(false);
    });
  }, [filters, collection, pageNum]);

  const setPageNumAndScroll = useCallback((newPageNum: number) => {
    setPageNum(newPageNum);
    document.getElementsByClassName("Polaris-IndexTable__Table")[0]?.scrollIntoView();
  }, []);
  const nextPage = useCallback(() => {
    if (hasNext) {
      setPageNumAndScroll(pageNum + 1);
    }
  }, [hasNext, pageNum, setPageNumAndScroll]);
  const prevPage = useCallback(() => {
    if (hasPrev) {
      setPageNumAndScroll(pageNum - 1);
    }
  }, [hasPrev, pageNum, setPageNumAndScroll]);

  const emptyState = useMemo(
    () => (
      <EmptyState
        action={{
          content: "Create purchase order",
          url: "/purchase_orders/new",
        }}
        heading="No purchase orders found"
        image=""
      />
    ),
    [],
  );

  const setFiltersResetPageNum = useCallback((newFilters: QueryParams) => {
    setFilters(newFilters);
    setPageNum(1);
  }, []);

  const setCollectionResetPageNum = useCallback((newCollection: number) => {
    setCollection(newCollection);
    setPageNum(1);
  }, []);

  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [exportFailed, setExportFailed] = useState(false);

  const pageMarkup = (
    <Page
      fullWidth
      primaryAction={{
        content: "Create purchase order",
        url: "/purchase_orders/new",
      }}
      secondaryActions={
        <Button
          disabled={isDownloadingCsv}
          icon={isDownloadingCsv ? <Spinner size="small" /> : undefined}
          onClick={() => {
            setIsDownloadingCsv(true);
            setExportFailed(false);
            const downloadFilters = {
              ...filters,
              by_state: COLLECTIONS[collection] === COLLECTIONS[TABS.ALL] ? undefined : COLLECTIONS[collection],
            };
            void downloadFile(`/purchase_orders.csv`, "Horse Purchase Orders.csv", downloadFilters)
              .catch((error: unknown) => {
                Rollbar.error(error);
                setExportFailed(true);
              })
              .finally(() => {
                setIsDownloadingCsv(false);
              });
          }}
          removeUnderline
          size="medium"
          variant="monochromePlain"
        >
          Export CSV
        </Button>
      }
      title="Purchase orders"
    >
      <ImportProgressBanner />
      {exportFailed ? (
        <Banner
          onDismiss={() => {
            setExportFailed(false);
          }}
          title="CSV export failed"
          tone="critical"
        >
          The purchase orders could not be exported. Please try again.
        </Banner>
      ) : null}
      <LegacyCard>
        <PurchaseOrdersSearchFieldAndMoreFilters
          columnSelection={{
            choices: paymentColumns.map((column) => ({ value: column.id, label: column.title })),
            selected: selectedColumns,
            onChange: setSelectedColumns,
          }}
          onSelect={setCollectionResetPageNum}
          selected={collection}
          setFilters={setFiltersResetPageNum}
        />
        <IndexTable
          emptyState={emptyState}
          headings={[
            ...columnNames,
            ...visibleColumns.map((column) => ({
              ...column,
              title: column.id === "amount_paid" ? column.title : (
                <span className="purchase-order-payment-heading">{column.title}</span>
              ),
            })),
          ]}
          itemCount={purchaseOrders.length}
          loading={isLoading}
          selectable={false}
        >
          <IndexTable.Row id="purchaseOrdersIndexTotalsRow" key={0} position={0} rowType="subheader">
            <IndexTable.Cell>Totals</IndexTable.Cell>
            <IndexTable.Cell />
            <IndexTable.Cell />
            <IndexTable.Cell />
            <IndexTable.Cell />
            <IndexTable.Cell />
            <IndexTable.Cell>{commaDelimiter(totals.quantity)}</IndexTable.Cell>
            <IndexTable.Cell>{commaDelimiter(totals.received)}</IndexTable.Cell>
            <IndexTable.Cell>{formatMoney(totals.total_cost, userCurrency, 2)}</IndexTable.Cell>
            {visibleColumns.map((column) => <IndexTable.Cell key={column.id} />)}
          </IndexTable.Row>
          {purchaseOrders.map((purchaseOrder, index) => (
            <IndexTable.Row id={purchaseOrder.id.toString()} key={purchaseOrder.id} position={index + 1}>
              <IndexTable.Cell>
                <HorseButton url={`/purchase_orders/${purchaseOrder.id}`} variant="plain">
                  {purchaseOrder.label || purchaseOrder.id.toString()}
                </HorseButton>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <span className="truncate" style={{ maxWidth: "250px" }}>
                  {purchaseOrder.vendor || purchaseOrder.supplierName}
                </span>
              </IndexTable.Cell>
              <IndexTable.Cell>{purchaseOrder.horse_location_name}</IndexTable.Cell>
              <IndexTable.Cell>{statusColor(purchaseOrder.status)}</IndexTable.Cell>
              <IndexTable.Cell>{formatDateStringToMMDYY(purchaseOrder.ordered_date)}</IndexTable.Cell>
              <IndexTable.Cell>{formatDateStringToMMDYY(purchaseOrder.date_received)}</IndexTable.Cell>
              <IndexTable.Cell>{assertNumber(purchaseOrder.quantity)}</IndexTable.Cell>
              <IndexTable.Cell>{assertNumber(purchaseOrder.received)}</IndexTable.Cell>
              <IndexTable.Cell>{formatMoney(purchaseOrder.cost, userCurrency, 2)}</IndexTable.Cell>
              {visibleColumns.map((column) => (
                <IndexTable.Cell key={column.id}>
                  <PurchaseOrderPaymentCell column={column} purchaseOrder={purchaseOrder} />
                </IndexTable.Cell>
              ))}
            </IndexTable.Row>
          ))}
        </IndexTable>
        <LegacyCard.Section>
          <LegacyStack alignment="center" distribution="center">
            <Pagination
              hasNext={hasNext}
              hasPrevious={hasPrev}
              nextKeys={[39]}
              onNext={nextPage}
              onPrevious={prevPage}
              previousKeys={[37]}
            />
          </LegacyStack>
        </LegacyCard.Section>
      </LegacyCard>
      <Footer pageTitle="purchase orders" url={purchaseOrderHelpPageUrl} />
    </Page>
  );

  return pageLoading ? <PurchaseOrdersIndexPageSkeleton /> : pageMarkup;
}

export default PurchaseOrdersIndex;
